
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { LoadingComponent } from '@commonComponents/loading/loading.component';
import { isNullish } from '@commonHelpers/math-utils';
import { CatalogIntegrationCatalogViewerDto, CatalogIntegrationType } from '@interfaces/HttpClient/CatalogApiPublicModels';
import { CatalogIntegrationSettingsService } from '@services/catalog-integration-settings.service';
import { CatalogIntegrationService } from '@services/catalog-integration.service';
import { ReferrerService } from '@services/referrer.service';
import { combineLatest } from 'rxjs';

@Component({
    selector: 'app-catalog-integration',
    templateUrl: './catalog-integration.component.html',
    styleUrls: ['./catalog-integration.component.scss'],
    providers: [CatalogIntegrationService],
    imports: [LoadingComponent]
})
export class CatalogIntegrationComponent implements OnInit {

	public loading = true
	public catalogKey: string
	public integrationKey: string

	constructor(
		private route: ActivatedRoute,
		private router: Router,
		protected catalogIntegrationService: CatalogIntegrationService,
		private catalogIntegrationSettingsService: CatalogIntegrationSettingsService,
		private referrerService: ReferrerService
	) { }

	ngOnInit(): void {
		const documentReferrer = document.referrer
		this.catalogIntegrationSettingsService.isInIntegrationMode = true;

		combineLatest([this.route.params, this.route.queryParams]).subscribe(async ([params, queryParams]) => {
			this.loading = true;


			// Bei der Integration von ADE in Herstellerseiten mittels Iframe wird ein Redirect verwendet. Zum Beispiel wird die URL
			// https://www.ausschreiben.de/initintegration/v2/catalog/ade_next?integrationKey=0b591f54-0bb6-44ef-8bab-d94dc184c548 umgeleitet auf https://www.ausschreiben.de/catalog/ade_next, sofern
			// der IntegrationKey korrekt ist und folglich eine Berechtigung zur Einbettung besteht. Durch diesen internen Redirect ändert sich allerdings der document.referrer des Aufrufs,
			// der letztlich getrackt werden soll, zu www.ausschreiben.de. Da die Herstellerseite als Referrer mitgeliefert werden soll, wird vor dieser Umleitung der document.referrer
			// des Iframes - also die URL der Herstellerseite - in einen Service weggespeichert. Hierzu wird der document.referer beim initialen Laden der Seite in eine Varaiable weggespeichert, damit
			// Änderungen durch spätere Navigationen sich nicht auf ihn auswirken.
			// Zusätzlich kann es vorkommen, dass im Backend bereits redirects stattgefunden haben. In diesem Fall wird der urprüngliche refferer als Query-Parameter mitgeliefert. Sollte dieser Queryparameter gesetzt sein,
			// dann wird dieser statt des document referrer in den Service gespeichert.
			this.referrerService.set(queryParams.referrer ?? documentReferrer)

			// Properties für Fehlermeldungen konfigurieren
			this.catalogKey = (params.key as string).toLowerCase()
			this.integrationKey = queryParams.integrationKey

			// Settings aus der Datenbank holen und in Service speichern. Hierbei wir gleichzeitig die Validität des integrationKeys überprüft. Nur falls dieser
			// valide ist, wird ein Ergebnis zurückgeliefert.
			const settings = await this.catalogIntegrationService.getCatalogIntegrationCatalogViewerSettings(this.catalogKey, queryParams.integrationKey)

			if (!settings) {
				await this.router.navigate(["notFound"], { replaceUrl: true })
				this.loading = false
				return
			}

			this.catalogIntegrationSettingsService.settings = settings
			this.catalogIntegrationSettingsService.catalogIntegrationType = CatalogIntegrationType.CatalogViewer
			this.catalogIntegrationSettingsService.integrationKey = queryParams.integrationKey

			// Redirect zu URL ohne das Segment "integration" ausführen. Das Url-Segment "integration" dient nur als Trigger, um die diese Komponente aufzurufen und die Settings abzuholen
			const catalogItemSegments = await this.handleUseCaseMarkCatalogItem(params, settings)
			const [searchSegment, searchQueryParams] = this.handleUseCaseSearch(params, queryParams, settings)

			await this.router.navigate(["catalog", this.catalogKey, ...catalogItemSegments, ...searchSegment], { queryParams: { ...searchQueryParams, integrationKey: queryParams.integrationKey }, replaceUrl: true })

			this.loading = false
		})
	}

	private async handleUseCaseMarkCatalogItem(params: Params, settings: CatalogIntegrationCatalogViewerDto): Promise<[string, string] | []> {
		if (!settings.canHighlightCatalogItem) { return [] }

		return params.catalogItemGuid ? ["catalogItem", params.catalogItemGuid] : []
	}

	private handleUseCaseSearch(params: Params, queryParams: Params, settings: CatalogIntegrationCatalogViewerDto): [string[], { searchTerm: any; } | { searchTerm?: undefined; }] | [] {
		if (!settings.canSearch) { return [[], {}] }

		const searchSegment = params?.view === "search" ? ["search"] : []
		const searchQueryParams = !isNullish(queryParams?.searchTerm) ? { searchTerm: queryParams?.searchTerm } : {}
		return [searchSegment, searchQueryParams]
	}
}
